import {
  Input,
  OnInit,
  Component,
  OnDestroy,
  ViewChild,
  ElementRef
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';

import { of, tap, filter, switchMap, catchError, take, Subscription } from 'rxjs';

import { CopySettingsRequest, Location as LocationModel } from '@shared/location/models';
import { CheckboxGroup } from '@tablesready/checkbox-group';
import {
  GlobalErrorHandler,
  ConfirmationModalComponent,
  ConfirmationModalResult,
} from '@tablesready/common-ui';
import { LocationApiService } from '../../../../../services';
import { SettingsToCopy } from '../../constants';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'tablesready-copy-settings-modal',
  templateUrl: './copy-settings-modal.component.html',
  styleUrls: ['./copy-settings-modal.component.scss'],
  providers: [GlobalErrorHandler],
})
export class CopySettingsModalComponent implements OnInit, OnDestroy {
  @ViewChild('contentRef') contentRef?: ElementRef;
  @ViewChild('alertRef') alertRef?: ElementRef;
  @ViewChild('leftTextRef') leftTextRef?: ElementRef;
  @ViewChild('textRef') textRef?: ElementRef;

  @Input() location: LocationModel;
  @Input() locations: LocationModel[] = [];

  icon = AppIcon;
  form: FormGroup;

  settingsToCopy = SettingsToCopy;
  locationsToDisplay: CheckboxGroup[] = [];

  private isMobileLandscape = false;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly fb: FormBuilder,
    private readonly alertsService: AlertsService,
    private readonly errorHandler: GlobalErrorHandler,
    private readonly locationApiService: LocationApiService,
    private readonly modalController: FrontendModalService,
    private readonly breakpointObserver: BreakpointObserver,
  ) {}

  ngOnInit(): void {
    this.locationsToDisplay = this.locations
      .filter(location => location._id !== this.location._id)
      .map(location => ({ name: location.name, value: location._id }));

    const breakpointObserverSub = this.breakpointObserver.observe(Breakpoints.HandsetLandscape)
      .pipe(tap(({ matches: isMobileLandscape}) => this.isMobileLandscape = isMobileLandscape)).subscribe();
    this.subscriptions.add(breakpointObserverSub);

    this.form = this.fb.group({
      locationIds: [[]],
      settings: [[]],
    });
  }

  save(): void {
    const value: CopySettingsRequest = this.form.value;

    if (value.locationIds.length < 1 || value.settings.length < 1) {
      this.alertsService.addError('You didn\'t provide required data.');

      return;
    }

    const targetLocations = value.locationIds.map(locationId => {
      const location = this.locations.find(location => location._id === locationId);

      return `<b>${location.name}</b>`;
    });

    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        content: `<p>You're about to copy and overwrite settings from <b>${this.location.name}</b> to ${targetLocations.join(', ')} location(-s). Do you want to continue?</p>`,
        closeBtnText: 'Cancel',
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        switchMap(() => this.locationApiService.copySettings({
          ...value,
          sourceId: this.location._id,
        })),
        tap(() => {
          this.alertsService.addSuccess('settings copied');
          this.close();
        }),
        catchError(err => {
          this.errorHandler.handleError(err);

          return of(null);
        })
      )
      .subscribe();
  }

  close(): void {
    this.modalController.dismiss();
  }

  get locationIdsControl(): AbstractControl {
    return this.form.get('locationIds');
  }

  get settingsControl(): AbstractControl {
    return this.form.get('settings');
  }

  get isFormInvalid(): boolean {
    return this.locationIdsControl.value.length === 0 || this.settingsControl.value.length === 0;
  }

  get contentLeftMaxHeight(): number {
    if (!this.isMobileLandscape) {
      return this.contentRef?.nativeElement.offsetHeight - this.alertRef?.nativeElement.offsetHeight - this.leftTextRef?.nativeElement.offsetHeight - 100;
    }

    return 100;
  }

  get contentRightMaxHeight(): number {
    if (!this.isMobileLandscape) {
      return this.contentRef?.nativeElement.offsetHeight - this.alertRef?.nativeElement.offsetHeight - this.textRef?.nativeElement.offsetHeight - 35;
    }

    return 250;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
